import {
  EntityInformation,
  ExportOfferingsCommand,
  RegionProfiling,
} from '@amzn/awsdev-docs-virtual-smiley-typescript-client';
import { Button, StatusIndicator } from '@amzn/awsui-components-react';
import { ReactElement, useCallback, useState } from 'react';
import { useAuthState } from '../authentication';
import { NoTokenError } from '../errors';
import { useLocation } from 'react-router-dom';

type ExportStatus = 'success' | 'loading' | 'error' | 'idle';
const texts = {
  success: 'Exported',
  loading: 'Loading',
  error: 'Error',
};
export const ExportEntityList = ({
  rows,
  columns,
}: {
  rows: EntityInformation[];
  columns: string[];
}): ReactElement => {
  const { token } = useAuthState();
  const [status, setStatus] = useState<ExportStatus>('idle');
  const reset = useCallback(() => setStatus('idle'), []);
  const location = useLocation();

  const getCellContent = (
    entity: EntityInformation,
    column: string
  ): string => {
    if (column === 'entityId') {
      if ('sharedText' in entity && entity.sharedText) {
        return entity.sharedText.entityId;
      } else if ('service' in entity && entity.service) {
        return entity.service.entityId;
      }
    }

    if (column === 'locale') {
      if ('sharedText' in entity && entity.sharedText) {
        return entity.sharedText.locale;
      } else if ('service' in entity && entity.service) {
        return entity.service.locale;
      }
    }

    if ('sharedText' in entity && entity.sharedText) {
      const value = entity.sharedText.value;
      const regionKey = column as RegionProfiling;
      const regionalValue = value[regionKey];
      return (
        regionalValue?.value
          .map((locString: { value: any }) => locString?.value)
          .join(' ') || ''
      );
    } else if ('service' in entity && entity.service) {
      const value = entity.service.value;
      const regionKey = column as RegionProfiling;
      const regionalValue = value[regionKey];
      return (
        regionalValue?.value
          .map((locString: { value: any }) => locString?.value)
          .join(' ') || ''
      );
    }

    return '';
  };

  const download = useCallback(() => {
    if (!token) throw new NoTokenError();
    setStatus('loading');

    console.log('Downloading: ', rows);
    const validColumns = columns.filter(
      (column) => column && column.trim() !== ''
    );
    const csvData = rows
      .map((entity) => {
        const rowData = columns.map((column) => getCellContent(entity, column));
        return rowData.some((content) => content.trim() !== '')
          ? rowData.join(',')
          : null;
      })
      .filter((row) => row !== null);

    const csvContent = [columns.join(','), ...csvData].join('\n');

    const a = document.createElement('a');
    a.href = URL.createObjectURL(
      new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    );
    a.setAttribute('target', '_blank');
    const fileName = location.pathname.includes('/shared-text')
      ? 'shared_text_entities'
      : 'service_entities';

    a.download = `${fileName}_${new Date().toISOString().split('.')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    setStatus('success');
    setTimeout(reset, 2000);
  }, [token, rows, columns]);

  return (
    <Button onClick={download} disabled={status !== 'idle'}>
      {status !== 'idle' ? (
        <StatusIndicator type={status}>{texts[status]}</StatusIndicator>
      ) : (
        'Export all'
      )}
    </Button>
  );
};
